import React, { useState, useEffect } from 'react';
import {
    SwatchIcon,
    BeakerIcon,
    GlobeAltIcon,
    ChartBarIcon,
    CogIcon,
    SparklesIcon,
    ClockIcon,
    CurrencyDollarIcon,
    ShieldCheckIcon,
    FireIcon,
    EyeIcon,
    MagnifyingGlassIcon,
    DocumentTextIcon,
    BuildingOfficeIcon,
    TruckIcon,
    ScaleIcon,
    SunIcon,
    CloudIcon,
    SnowflakeIcon
} from '@heroicons/react/24/outline';
import { authorizedFetch } from '../utils/api';

const FabricIntelligence = () => {
    const [activeTab, setActiveTab] = useState('analysis');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [renderTime, setRenderTime] = useState(null);

    // State for fabric intelligence features
    const [fabricAnalysis, setFabricAnalysis] = useState({});
    const [materialTrends, setMaterialTrends] = useState([]);
    const [supplierData, setSupplierData] = useState([]);
    const [fabricInventory, setFabricInventory] = useState([]);
    const [qualityMetrics, setQualityMetrics] = useState({});
    const [sustainabilityData, setSustainabilityData] = useState({});

    const backendUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'https://suanfamama-fashion-buyer-production.up.railway.app';

    useEffect(() => {
        const startTime = performance.now();
        fetchFabricData().finally(() => {
            const endTime = performance.now();
            setRenderTime(Math.round(endTime - startTime));
        });
    }, []);

    const fetchFabricData = async () => {
        setLoading(true);
        try {
            const [analysisResp, trendsResp, suppliersResp, inventoryResp, qualityResp, sustainabilityResp] = await Promise.all([
                authorizedFetch(`${backendUrl}/fabric-intelligence/analysis`),
                authorizedFetch(`${backendUrl}/fabric-intelligence/material-trends`),
                authorizedFetch(`${backendUrl}/fabric-intelligence/suppliers`),
                authorizedFetch(`${backendUrl}/fabric-intelligence/inventory`),
                authorizedFetch(`${backendUrl}/fabric-intelligence/quality-metrics`),
                authorizedFetch(`${backendUrl}/fabric-intelligence/sustainability`)
            ]);

            setFabricAnalysis(analysisResp.data || {});
            setMaterialTrends(trendsResp.data || []);
            setSupplierData(suppliersResp.data || []);
            setFabricInventory(inventoryResp.data || []);
            setQualityMetrics(qualityResp.data || {});
            setSustainabilityData(sustainabilityResp.data || {});
        } catch (err) {
            console.error('Error fetching fabric data:', err);
            setError('Failed to load fabric intelligence data');
        } finally {
            setLoading(false);
        }
    };

    const LoadingSkeleton = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const FabricAnalysisView = () => (
        <div className="space-y-6">
            {/* Fabric Intelligence Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <SwatchIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">2,847</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Fabric Types Analyzed</div>
                        </div>
                    </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <BeakerIcon className="h-8 w-8 text-green-600 dark:text-green-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">94.7%</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Quality Accuracy</div>
                        </div>
                    </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <GlobeAltIcon className="h-8 w-8 text-purple-600 dark:text-purple-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">156</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Global Suppliers</div>
                        </div>
                    </div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <CurrencyDollarIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">$2.8M</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Fabric Investment</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fabric Composition Analysis */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                        <BeakerIcon className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                        Advanced Fabric Composition Analysis
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Professional textile analysis with detailed fiber composition and quality metrics
                    </p>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Fabric Analyzer */}
                        <div className="space-y-4">
                            <h4 className="font-medium text-gray-900 dark:text-white">Fabric Analyzer</h4>

                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                                <SwatchIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 dark:text-gray-400 mb-4">
                                    Upload fabric sample or enter fabric details
                                </p>
                                <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
                                    Analyze Fabric
                                </button>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Fabric Type
                                    </label>
                                    <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                        <option>Cotton Blend</option>
                                        <option>Pure Wool</option>
                                        <option>Silk Crepe</option>
                                        <option>Linen Blend</option>
                                        <option>Synthetic Performance</option>
                                        <option>Cashmere</option>
                                        <option>Denim</option>
                                        <option>Jersey Knit</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Weight (GSM)
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="e.g., 180"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Thread Count
                                    </label>
                                    <input
                                        type="number"
                                        placeholder="e.g., 200"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Analysis Results */}
                        <div className="space-y-4">
                            <h4 className="font-medium text-gray-900 dark:text-white">Analysis Results</h4>

                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <h5 className="font-medium text-gray-900 dark:text-white mb-3">
                                    Premium Cotton Blend Analysis
                                </h5>

                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Cotton Content:</span>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">85%</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Elastane:</span>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">15%</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Thread Count:</span>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">220 TPI</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Weight:</span>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">180 GSM</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Quality Grade:</span>
                                        <span className="text-sm font-medium text-green-600 dark:text-green-400">Premium A+</span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                    <div className="flex items-center mb-2">
                                        <ShieldCheckIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                            Durability Score: 9.2/10
                                        </span>
                                    </div>
                                    <p className="text-xs text-blue-800 dark:text-blue-200">
                                        Excellent wash resistance and color fastness
                                    </p>
                                </div>

                                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                    <div className="flex items-center mb-2">
                                        <SunIcon className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                                        <span className="text-sm font-medium text-green-900 dark:text-green-100">
                                            Seasonal Suitability
                                        </span>
                                    </div>
                                    <p className="text-xs text-green-800 dark:text-green-200">
                                        Perfect for Spring/Summer collections
                                    </p>
                                </div>

                                <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                                    <div className="flex items-center mb-2">
                                        <CurrencyDollarIcon className="h-4 w-4 text-purple-600 dark:text-purple-400 mr-2" />
                                        <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                                            Cost Analysis
                                        </span>
                                    </div>
                                    <p className="text-xs text-purple-800 dark:text-purple-200">
                                        $12.50/yard - Premium pricing justified by quality
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fabric Performance Metrics */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Professional Fabric Performance Metrics
                    </h3>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                name: "Premium Wool Suiting",
                                composition: "100% Merino Wool",
                                weight: "280 GSM",
                                threadCount: "Super 120s",
                                durability: 9.4,
                                comfort: 9.1,
                                drape: 9.6,
                                price: "$45.00/yard",
                                season: "Fall/Winter",
                                care: "Dry Clean Only",
                                origin: "Italy",
                                sustainability: "A+"
                            },
                            {
                                name: "Luxury Silk Crepe",
                                composition: "100% Mulberry Silk",
                                weight: "140 GSM",
                                threadCount: "22 Momme",
                                durability: 8.2,
                                comfort: 9.8,
                                drape: 9.9,
                                price: "$68.00/yard",
                                season: "Year-Round",
                                care: "Hand Wash/Dry Clean",
                                origin: "China",
                                sustainability: "B+"
                            },
                            {
                                name: "Organic Cotton Denim",
                                composition: "98% Organic Cotton, 2% Elastane",
                                weight: "320 GSM",
                                threadCount: "14 oz",
                                durability: 9.7,
                                comfort: 8.4,
                                drape: 7.8,
                                price: "$18.50/yard",
                                season: "Year-Round",
                                care: "Machine Wash",
                                origin: "Turkey",
                                sustainability: "A+"
                            }
                        ].map((fabric, index) => (
                            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                                    {fabric.name}
                                </h4>

                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Composition:</span>
                                        <span className="text-gray-900 dark:text-white font-medium">{fabric.composition}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Weight:</span>
                                        <span className="text-gray-900 dark:text-white">{fabric.weight}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Thread Count:</span>
                                        <span className="text-gray-900 dark:text-white">{fabric.threadCount}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Origin:</span>
                                        <span className="text-gray-900 dark:text-white">{fabric.origin}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Price:</span>
                                        <span className="text-gray-900 dark:text-white font-medium">{fabric.price}</span>
                                    </div>
                                </div>

                                <div className="mt-4 space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-600 dark:text-gray-400">Durability</span>
                                        <span className="text-xs font-medium text-gray-900 dark:text-white">{fabric.durability}/10</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                        <div
                                            className="bg-blue-600 h-1.5 rounded-full"
                                            style={{ width: `${fabric.durability * 10}%` }}
                                        ></div>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-600 dark:text-gray-400">Comfort</span>
                                        <span className="text-xs font-medium text-gray-900 dark:text-white">{fabric.comfort}/10</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                        <div
                                            className="bg-green-600 h-1.5 rounded-full"
                                            style={{ width: `${fabric.comfort * 10}%` }}
                                        ></div>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-600 dark:text-gray-400">Drape</span>
                                        <span className="text-xs font-medium text-gray-900 dark:text-white">{fabric.drape}/10</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                                        <div
                                            className="bg-purple-600 h-1.5 rounded-full"
                                            style={{ width: `${fabric.drape * 10}%` }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between mt-4">
                                    <span className={`px-2 py-1 rounded-full text-xs ${fabric.sustainability === 'A+' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                        fabric.sustainability === 'B+' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                                            'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                                        }`}>
                                        Sustainability: {fabric.sustainability}
                                    </span>
                                    <div className="flex space-x-1">
                                        <button className="p-1 text-gray-400 hover:text-blue-600">
                                            <EyeIcon className="h-4 w-4" />
                                        </button>
                                        <button className="p-1 text-gray-400 hover:text-green-600">
                                            <DocumentTextIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const MaterialTrendsView = () => (
        <div className="space-y-6">
            {/* Trend Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">Hot</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Sustainable Fibers</div>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">Rising</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Performance Blends</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">Stable</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Natural Fibers</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">Declining</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Fast Fashion Synthetics</div>
                </div>
            </div>

            {/* Material Trend Analysis */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        2024 Material Trend Intelligence
                    </h3>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <FireIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Advanced Material Trend Forecasting
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            AI-powered analysis of emerging fabric trends, sustainability shifts, and material innovations.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const SupplierNetworkView = () => (
        <div className="space-y-6">
            {/* Supplier Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">156</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Global Suppliers</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">98.2%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">On-Time Delivery</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">47</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Countries</div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">A+</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Avg Quality Rating</div>
                </div>
            </div>

            {/* Supplier Network */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Global Fabric Supplier Network
                    </h3>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Professional Supplier Management
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Comprehensive supplier database with quality ratings, delivery performance, and pricing intelligence.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const SustainabilityView = () => (
        <div className="space-y-6">
            {/* Sustainability Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="flex items-center">
                        <SparklesIcon className="h-8 w-8 text-green-600 dark:text-green-400 mr-3" />
                        <div>
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">73%</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Sustainable Materials</div>
                        </div>
                    </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">-42%</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Carbon Footprint</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">89</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Certified Suppliers</div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">A</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">ESG Rating</div>
                </div>
            </div>

            {/* Sustainability Dashboard */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Fabric Sustainability Intelligence
                    </h3>
                </div>
                <div className="p-6">
                    <div className="text-center py-8">
                        <SparklesIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Sustainable Material Tracking
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Comprehensive sustainability analysis, certifications, and environmental impact tracking.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const tabs = [
        { id: 'analysis', label: 'Fabric Analysis', icon: BeakerIcon },
        { id: 'trends', label: 'Material Trends', icon: FireIcon },
        { id: 'suppliers', label: 'Supplier Network', icon: BuildingOfficeIcon },
        { id: 'sustainability', label: 'Sustainability', icon: SparklesIcon }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <LoadingSkeleton />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-500 text-xl mb-4">⚠️ Error</div>
                    <p className="text-gray-600 dark:text-gray-400">{error}</p>
                    <button
                        onClick={fetchFabricData}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                                <SwatchIcon className="h-8 w-8 mr-3 text-blue-600 dark:text-blue-400" />
                                Fabric & Material Intelligence
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mt-2">
                                Professional textile analysis and material intelligence for worldwide fashion buyers
                            </p>
                        </div>
                        {renderTime && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Loaded in {renderTime}ms
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation Tabs */}
                <div className="mb-8">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="-mb-px flex space-x-8">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                            }`}
                                    >
                                        <Icon className="h-4 w-4 mr-2" />
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="space-y-6">
                    {activeTab === 'analysis' && <FabricAnalysisView />}
                    {activeTab === 'trends' && <MaterialTrendsView />}
                    {activeTab === 'suppliers' && <SupplierNetworkView />}
                    {activeTab === 'sustainability' && <SustainabilityView />}
                </div>
            </div>
        </div>
    );
};

export default FabricIntelligence;
